import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ShieldAlert, MessageSquare, RefreshCw, Radio } from "lucide-react";

const fmt = (iso) => { try { return new Date(iso).toLocaleString(); } catch { return iso; } };

export function DenialLogPanel() {
  const [data, setData] = useState(null);
  const load = async () => {
    try { const { data } = await api.get("/admin/perm-denials?limit=100"); setData(data); }
    catch { toast.error("Failed to load"); }
  };
  useEffect(() => { load(); }, []);
  if (!data) return <div className="text-slate-500 text-sm">Loading…</div>;
  return (
    <div className="space-y-4" data-testid="denial-log-panel">
      {data.top_last_hour && data.top_last_hour.length > 0 && (
        <div className="bg-red-500/5 border border-red-500/30 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-3">
            <ShieldAlert className="w-4 h-4 text-red-400" />
            <div className="text-sm font-semibold text-red-300">Top denials — last hour</div>
          </div>
          <div className="grid gap-2">
            {data.top_last_hour.map((t) => (
              <div key={t.email} className="flex items-center gap-3 text-xs font-mono">
                <Badge className="bg-red-500/20 text-red-300 border-red-500/40 border font-mono">{t.count}</Badge>
                <span className="text-slate-200 flex-1 truncate">{t.email}</span>
                <span className="text-slate-500 truncate max-w-[45%]">{t.perms.join(", ")}</span>
              </div>
            ))}
          </div>
        </div>
      )}
      <div className="flex justify-between items-center">
        <div className="text-xs uppercase tracking-widest text-slate-500 font-mono">Last {data.recent.length} denials</div>
        <Button onClick={load} size="sm" variant="outline" className="bg-transparent border-white/10 text-slate-300 h-8"><RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Refresh</Button>
      </div>
      <div className="bg-black/20 border border-white/5 rounded-lg overflow-hidden">
        <div className="max-h-[420px] overflow-auto">
          <table className="w-full text-xs">
            <thead className="sticky top-0 bg-black/60 backdrop-blur">
              <tr className="text-slate-500 uppercase tracking-widest text-[10px] font-mono">
                <th className="text-left px-3 py-2 font-normal">When</th>
                <th className="text-left px-3 py-2 font-normal">Email</th>
                <th className="text-left px-3 py-2 font-normal">Perm</th>
                <th className="text-left px-3 py-2 font-normal">Path</th>
              </tr>
            </thead>
            <tbody>
              {data.recent.length === 0 && (
                <tr><td colSpan="4" className="text-center text-slate-500 py-8" data-testid="denial-log-empty">No denials logged yet 🎉</td></tr>
              )}
              {data.recent.map((r) => (
                <tr key={r.id} className="border-t border-white/5" data-testid={`denial-row-${r.id}`}>
                  <td className="px-3 py-2 text-slate-400 font-mono">{fmt(r.at)}</td>
                  <td className="px-3 py-2 text-slate-200">{r.email}</td>
                  <td className="px-3 py-2 text-amber-300 font-mono">{r.perm}</td>
                  <td className="px-3 py-2 text-slate-500 font-mono">{r.path}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export function SmsOutboxPanel() {
  const [data, setData] = useState(null);
  const load = async () => {
    try { const { data } = await api.get("/admin/sms-outbox?limit=50"); setData(data); }
    catch { toast.error("Failed to load SMS outbox"); }
  };
  useEffect(() => { load(); }, []);
  if (!data) return <div className="text-slate-500 text-sm">Loading…</div>;
  return (
    <div className="space-y-4" data-testid="sms-outbox-panel">
      <div className="flex items-center gap-2">
        {data.mock_mode ? (
          <Badge className="bg-amber-400/10 border-amber-400/30 text-amber-300 border font-mono uppercase text-[10px] tracking-widest"><Radio className="w-3 h-3 mr-1" /> MOCK MODE</Badge>
        ) : (
          <Badge className="bg-emerald-500/10 border-emerald-500/30 text-emerald-300 border font-mono uppercase text-[10px] tracking-widest">LIVE</Badge>
        )}
        <div className="text-xs text-slate-500">
          {data.mock_mode
            ? "Twilio not configured — messages are queued here instead of being sent."
            : "Real Twilio SMS: this is a log of the last 50 messages we sent."}
        </div>
        <div className="flex-1" />
        <Button onClick={load} size="sm" variant="outline" className="bg-transparent border-white/10 text-slate-300 h-8"><RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Refresh</Button>
      </div>
      <div className="bg-black/20 border border-white/5 rounded-lg overflow-hidden">
        <div className="max-h-[420px] overflow-auto">
          <table className="w-full text-xs">
            <thead className="sticky top-0 bg-black/60 backdrop-blur">
              <tr className="text-slate-500 uppercase tracking-widest text-[10px] font-mono">
                <th className="text-left px-3 py-2 font-normal">When</th>
                <th className="text-left px-3 py-2 font-normal">To</th>
                <th className="text-left px-3 py-2 font-normal">Body</th>
                <th className="text-right px-3 py-2 font-normal">Status</th>
              </tr>
            </thead>
            <tbody>
              {data.messages.length === 0 && (
                <tr><td colSpan="4" className="text-center text-slate-500 py-8" data-testid="sms-outbox-empty">No SMS activity yet.</td></tr>
              )}
              {data.messages.map((m) => (
                <tr key={m.id} className="border-t border-white/5" data-testid={`sms-row-${m.id}`}>
                  <td className="px-3 py-2 text-slate-400 font-mono">{fmt(m.at)}</td>
                  <td className="px-3 py-2 text-slate-200 font-mono">{m.to}</td>
                  <td className="px-3 py-2 text-slate-300 max-w-[400px] truncate" title={m.body}>{m.body}</td>
                  <td className="px-3 py-2 text-right">
                    <Badge className={m.sent
                      ? "bg-emerald-500/15 text-emerald-300 border-emerald-500/30 border font-mono text-[10px]"
                      : "bg-amber-400/10 text-amber-300 border-amber-400/30 border font-mono text-[10px]"}>
                      {m.sent ? "SENT" : "MOCK"}
                    </Badge>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
